// Copyright 2025-2026 - Hewlett Packard Enterprise Company
import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, DropButton, Button, Heading } from 'grommet';
import { Chat, Close, Contract, Expand, More } from '@hpe-design/icons-grommet';
import PropTypes from 'prop-types';
import ExternalAnchor from '../ExternalAnchor/ExternalAnchor';
import { ChatContext } from '../../providers/ChatProvider';
import { SidebarContext } from '../../providers/SidebarProvider';

export default function ChatHeader({ onClose }) {
  const { t } = useTranslation('common');
  const [openDropMenu, setOpenDropMenu] = useState();
  const { clearConversations } = useContext(ChatContext);
  const { isChatExpanded, toggleIsChatExpanded } = useContext(SidebarContext);
  const { setInsightsMode, setInsightsSessionInitialized } =
    useContext(SidebarContext);

  const onCloseChatBot = () => {
    // treat escape/clicking outside the same
    onClose();
  };

  return (
    <Box
      data-testid="chat-header"
      justify="between"
      flex={false}
      gap="xsmall"
      background="background-front"
      style={{
        position: 'sticky',
        top: 0,
      }}
      direction="row"
      pad={{ right: 'xsmall' }}
    >
      <Box direction="row">
        <Button
          default
          icon={isChatExpanded ? <Contract /> : <Expand />}
          onClick={toggleIsChatExpanded}
          tip={isChatExpanded ? t('chat.contract_chat') : t('chat.expand_chat')}
          a11yTitle={
            isChatExpanded ? t('chat.contract_chat') : t('chat.expand_chat')
          }
          data-testid="chat-expand-toggle"
        />
        <DropButton
          label={<More size="small" />}
          open={openDropMenu}
          onOpen={() => setOpenDropMenu(true)}
          onClose={() => setOpenDropMenu(false)}
          dropContent={
            <>
              <Button
                default
                kind="option"
                icon={<Chat />}
                label={t('chat.new_chat')}
                onClick={() => {
                  clearConversations(); // Clear the conversations
                  setInsightsMode(false);
                  setInsightsSessionInitialized(false);
                  setOpenDropMenu(false);
                }}
                data-testid="clear-conversations-button"
                style={{
                  display: 'flex',
                }}
              />
              <Box border="top" pad="3xsmall">
                <Button default>
                  <ExternalAnchor href="https://www.hpe.com/us/en/legal/privacy.html#privacystatement">
                    {t('chat.hpe_privacy_policy')}
                  </ExternalAnchor>
                </Button>
              </Box>
            </>
          }
          dropAlign={{
            left: 'left',
            top: 'bottom',
          }}
          data-testid="chat-header-drop-button"
        />
      </Box>
      <Box direction="row" gap="5xsmall" alignSelf="center">
        <Heading margin="none" level={2}>
          {t('chat.chat_title')}
        </Heading>
      </Box>
      <Button default icon={<Close />} onClick={onCloseChatBot} />
    </Box>
  );
}

ChatHeader.propTypes = {
  onClose: PropTypes.func.isRequired,
};
